import { useMemo, useState } from "react";
import { API_BASE } from "@/lib/api";
import { HELP_SECTIONS } from "@/lib/helpContent";
import { FilePdf, MagnifyingGlass, BookOpen } from "@phosphor-icons/react";

export default function Help() {
  const [q, setQ] = useState("");
  const sections = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return HELP_SECTIONS;
    return HELP_SECTIONS.filter((s) => (s.title + " " + s.keywords).toLowerCase().includes(t));
  }, [q]);

  const jump = (id) => document.getElementById(`help-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="p-4 md:p-8" data-testid="help-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
            <BookOpen size={26} weight="duotone" className="text-green-600" /> Admin Guide
          </h1>
          <p className="text-sm text-slate-500 mt-1">How the whole system fits together — for current and future administrators.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={`${API_BASE}/downloads/admin-guide`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"
            data-testid="download-admin-guide-btn"
          >
            <FilePdf size={18} /> User's Guide (PDF)
          </a>
          <a
            href={`${API_BASE}/downloads/admin-quickref`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold"
            data-testid="download-admin-quickref-btn"
          >
            <FilePdf size={18} /> Quick Reference (PDF)
          </a>
        </div>
      </div>

      <div className="relative max-w-md mb-6">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the guide… (e.g. invoice, pay run, supervisor)"
          className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          data-testid="help-search-input"
        />
      </div>

      <div className="lg:flex lg:gap-8">
        <aside className="hidden lg:block w-72 flex-none">
          <div className="sticky top-6 bg-white border border-slate-200 rounded-lg p-4 max-h-[85vh] overflow-y-auto">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2">Contents</div>
            <nav className="space-y-0.5">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => jump(s.id)}
                  data-testid={`help-toc-${s.id}`}
                  className="block w-full text-left text-[13px] text-slate-600 hover:text-green-700 hover:bg-green-50 rounded px-2 py-1.5 leading-snug"
                >
                  {s.title}
                </button>
              ))}
              {sections.length === 0 && <div className="text-sm text-slate-400 px-2 py-1">No matches.</div>}
            </nav>
          </div>
        </aside>

        <div className="flex-1 min-w-0 space-y-6">
          {sections.map((s) => (
            <section
              key={s.id}
              id={`help-${s.id}`}
              data-testid={`help-section-${s.id}`}
              className="bg-white border border-slate-200 rounded-lg p-5 md:p-6 scroll-mt-6"
            >
              <h2 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-slate-100">{s.title}</h2>
              {s.body}
            </section>
          ))}
          {sections.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500 text-sm" data-testid="help-no-results">
              Nothing matches "{q}". Try a different word — or clear the search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
